import { type FC, memo, useEffect } from 'react';
import { isEmpty, values, first } from 'lodash';
// Import Types
import { SegmentFilterUIModel } from 'types';
// Import Store
import { useAppDispatch, useAppSelector } from 'store';
import {
    selectBaseQueryConfigurations,
    selectExclusionRulesQueryConfigurationsState,
} from 'store/slices/queryConfigurations';
import { changeDynamicSegmentActiveTabIndex, selectDynamicSegmentActiveTabIndex } from 'store/slices/dynamicSegment';
// Import UI Components
import { BusyLoader, Tabs, Tab, Tooltip } from '@geneui/components';
// Import Components
import FilterSchemaList from './FiltersContainer/FilterSchemaList';

interface DynamicSegmentInfoProps {
    segmentFiltersData: Nullable<Record<string, SegmentFilterUIModel>>;
    isExclusionRules?: boolean;
}

const DynamicSegmentInfo: FC<DynamicSegmentInfoProps> = ({ segmentFiltersData, isExclusionRules }) => {
    const dispatch = useAppDispatch();
    const queryConfigurations = useAppSelector(
        isExclusionRules ? selectExclusionRulesQueryConfigurationsState : selectBaseQueryConfigurations,
    );
    const tabActiveIndex = useAppSelector(selectDynamicSegmentActiveTabIndex);

    useEffect(() => {
        dispatch(changeDynamicSegmentActiveTabIndex(0));
    }, [dispatch]);

    const tabChangeHandler = (id) => {
        dispatch(changeDynamicSegmentActiveTabIndex(id));
    };

    return (
        <BusyLoader
            isBusy={isEmpty(queryConfigurations) || isEmpty(segmentFiltersData)}
            type="spinner"
            spinnerSize="medium"
            className="crm-justify-content-center"
        >
            {values(segmentFiltersData).length > 1 ? (
                <Tabs type="box" activeKey={tabActiveIndex} onChange={tabChangeHandler}>
                    {values(segmentFiltersData).map((subSegment) => {
                        return (
                            <Tab
                                key={subSegment.seqId}
                                className={`${+tabActiveIndex === subSegment.seqId ? 'active' : ''}`}
                                title={
                                    <Tooltip text={subSegment.name}>
                                        <div className="ellipsis-text">
                                            <button onClick={() => tabChangeHandler(subSegment.seqId)}>
                                                <p>
                                                    {+tabActiveIndex === subSegment.seqId
                                                        ? subSegment.name
                                                        : subSegment.name.slice(0, 4)}
                                                </p>
                                            </button>
                                        </div>
                                    </Tooltip>
                                }
                            >
                                <FilterSchemaList
                                    filtersData={subSegment}
                                    queryConfigurations={queryConfigurations}
                                    withoutActions={true}
                                />
                            </Tab>
                        );
                    })}
                </Tabs>
            ) : (
                <div>
                    <FilterSchemaList
                        filtersData={first(values(segmentFiltersData)) ?? {}}
                        queryConfigurations={queryConfigurations}
                        withoutActions={true}
                    />
                </div>
            )}
        </BusyLoader>
    );
};

export default memo(DynamicSegmentInfo);
